import { useDispatch, useSelector } from 'react-redux';
import {
    createMeasurement,
    editMeasurement,
    removeMeasurement,
} from '../../../../store/slices/measurementsSlice/action';
import { convertToNumber } from '../../helpers/convertToNumber';
import { useContext } from 'react';
import { MeasuremntsContext } from '../../Context/MeasuremntsContext';
import { useTranslation } from 'react-i18next';

const extensions = ['id', 'guid'];
export const useMeasurementsModalFooter = (onHideModal) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const {
        isManualCalculations,
        calculations,
        manualCalculations,
        openingsList,
        additionallyList,
        currentCardId,
        isEdited,
    } = useSelector((state) => state.measurementsReducer);

    const isEdit = !!currentCardId;

    const {
        imageList,
        openingsDelete,
        additionallyDelete,
        imagesDelete,
        setErrorsInput,
        showPopup,
        hidePopup,
    } = useContext(MeasuremntsContext);

    const onSubmit = async () => {
        const formData = new FormData();

        const additionallyEmptyName = Object.values(additionallyList.entities)
            .filter((additionally) => !additionally.name)
            .map((additionally) => additionally.id);

        if (!calculations.name || !!additionallyEmptyName.length) {
            let name = null;
            let additionallyName = {};
            if (!!additionallyEmptyName.length) {
                additionallyEmptyName.forEach((id) => {
                    additionallyName[id] = t(
                        'useMeasurementsModalFooter.onSubmit.additionallyName'
                    );
                });
            }

            if (!calculations.name) {
                name = t('useMeasurementsModalFooter.onSubmit.calculations.name');
            }

            setErrorsInput({ name, additionallyName });
            return;
        }

        setErrorsInput({ additionallyName: {}, name: null });

        if (isManualCalculations) {
            const editedManualCalculation = {
                ...manualCalculations,
                in_edit_mode: 1,
                width: convertToNumber(calculations.width),
                length: convertToNumber(calculations.length),
                height: convertToNumber(calculations.height),
                name: calculations.name,
                comment: calculations.comment,
                floor_area: convertToNumber(manualCalculations.floor_area),
                ceiling_area: convertToNumber(manualCalculations.ceiling_area),
                wall_area: convertToNumber(manualCalculations.wall_area),
                perimeter: convertToNumber(manualCalculations.perimeter),
                without_opening: convertToNumber(manualCalculations.without_opening),
                volume: convertToNumber(manualCalculations.volume),
                door_quantity: convertToNumber(manualCalculations.door_quantity),
                door_area: convertToNumber(manualCalculations.door_area),
                door_perimeter: convertToNumber(manualCalculations.door_perimeter),
                door_slope_area: convertToNumber(manualCalculations.door_slope_area),
                door_slope_perimeter: convertToNumber(manualCalculations.door_slope_perimeter),
                window_quantity: convertToNumber(manualCalculations.window_quantity),
                window_area: convertToNumber(manualCalculations.window_area),
                window_perimeter: convertToNumber(manualCalculations.window_perimeter),
                window_slope_area: convertToNumber(manualCalculations.window_slope_area),
                window_slope_perimeter: convertToNumber(manualCalculations.window_slope_perimeter),
            };
            Object.entries(editedManualCalculation).forEach(([key, value]) => {
                if (!extensions.includes(key)) {
                    formData.append(key, value);
                }
            });
        } else {
            const editedCalculation = {
                ...calculations,
                in_edit_mode: 0,
                width: convertToNumber(calculations.width),
                length: convertToNumber(calculations.length),
                height: convertToNumber(calculations.height),
                floor_area: convertToNumber(calculations.floor_area),
                ceiling_area: convertToNumber(calculations.ceiling_area),
                wall_area: convertToNumber(calculations.wall_area),
                perimeter: convertToNumber(calculations.perimeter),
                without_opening: convertToNumber(calculations.without_opening),
                volume: convertToNumber(calculations.volume),
                door_quantity: convertToNumber(calculations.door_quantity),
                door_area: convertToNumber(calculations.door_area),
                door_perimeter: convertToNumber(calculations.door_perimeter),
                door_slope_area: convertToNumber(calculations.door_slope_area),
                door_slope_perimeter: convertToNumber(calculations.door_slope_perimeter),
                window_quantity: convertToNumber(calculations.window_quantity),
                window_area: convertToNumber(calculations.window_area),
                window_perimeter: convertToNumber(calculations.window_perimeter),
                window_slope_area: convertToNumber(calculations.window_slope_area),
                window_slope_perimeter: convertToNumber(calculations.window_slope_perimeter),
            };

            Object.entries(editedCalculation).forEach(([key, value]) => {
                if (!extensions.includes(key)) {
                    formData.append(key, key === 'in_edit_mode' ? (value ? 1 : 0) : value);
                }
            });
        }

        if (isEdit) {
            Object.values(openingsList.entities)
                .filter((opening) => !!opening.isNew)
                .forEach((opening, index) => {
                    const validateKeys = ['type', 'height', 'width', 'depth', 'floor_distance'];
                    Object.entries(opening).forEach(([key, value]) => {
                        if (validateKeys.includes(key)) {
                            const formKey =
                                key === 'height'
                                    ? `openings[new][${index}][length]`
                                    : `openings[new][${index}][${key}]`;
                            formData.append(
                                formKey,
                                key === 'type' ? value : convertToNumber(value)
                            );
                        }
                    });
                });
            Object.values(openingsList.entities)
                .filter((opening) => !opening.isNew)
                .forEach((opening, index) => {
                    const validateKeys = [
                        'type',
                        'height',
                        'width',
                        'depth',
                        'floor_distance',
                        'id',
                    ];

                    Object.entries(opening).forEach(([key, value]) => {
                        if (validateKeys.includes(key)) {
                            const formKey =
                                key === 'height'
                                    ? `openings[edit][${index}][length]`
                                    : `openings[edit][${index}][${key}]`;
                            formData.append(
                                formKey,
                                key === 'type' ? value : convertToNumber(value)
                            );
                        }
                    });
                });
            openingsDelete.forEach((id, index) => {
                formData.append(`openings[delete][${index}][id]`, id);
            });

            Object.values(additionallyList.entities)
                .filter((additionally) => !!additionally.isNew)
                .forEach((additionally, index) => {
                    Object.entries(additionally).forEach(([key, value]) => {
                        if (key !== 'id' && key !== 'isNew') {
                            formData.append(
                                `dimensions[new][${index}][${key}]`,
                                key !== 'value' ? value : convertToNumber(value)
                            );
                        }
                    });
                });
            Object.values(additionallyList.entities)
                .filter((additionally) => !additionally.isNew)
                .forEach((additionally, index) => {
                    Object.entries(additionally).forEach(([key, value]) => {
                        formData.append(
                            `dimensions[edit][${index}][${key}]`,
                            key !== 'value' ? value : convertToNumber(value)
                        );
                    });
                });
            additionallyDelete.forEach((id, index) => {
                formData.append(`dimensions[delete][${index}][id]`, id);
            });
            imagesDelete.forEach((id, index) => {
                formData.append(`attachments[delete][${index}]`, id);
            });
            [...imageList].reverse().forEach((image, index) => {
                if (!image.type) formData.append(`attachments[new][${index}]`, image.file);
            });
            // attachments[new][0]
            await dispatch(editMeasurement({ formData, measurementId: currentCardId }));
        } else {
            Object.values(openingsList.entities).forEach((opening, index) => {
                const validateKeys = ['type', 'height', 'width', 'depth', 'floor_distance'];
                Object.entries(opening).forEach(([key, value]) => {
                    if (validateKeys.includes(key)) {
                        const formKey =
                            key === 'height'
                                ? `openings[${index}][length]`
                                : `openings[${index}][${key}]`;
                        formData.append(formKey, key === 'type' ? value : convertToNumber(value));
                    }
                });
            });
            Object.values(additionallyList.entities).forEach((additionally, index) => {
                Object.entries(additionally).forEach(([key, value]) => {
                    if (key !== 'id' && key !== 'isNew') {
                        formData.append(
                            `dimensions[${index}][${key}]`,
                            key !== 'value' ? value : convertToNumber(value)
                        );
                    }
                });
            });

            [...imageList].reverse().forEach((image) => {
                formData.append('attachments[]', image.file);
            });
            await dispatch(createMeasurement(formData));
        }
        await onHideModal(false);
    };
    const onDelete = () => {
        showPopup({
            needCrossIcon: false,
            text: t('useMeasurementsModalFooter.onDelete.text'),
            danger: t('button.delete.text'),
            cancel: t('button.cancel.text'),
            onSubmit: async () => {
                await hidePopup();
                await dispatch(removeMeasurement(currentCardId));
                await onHideModal(false);
            },
            onCancel: () => hidePopup(),
        });
    };

    return { onSubmit, onDelete, isEdit, isEdited };
};
